/**
 * Locale, direction and theme.
 *
 * Direction is applied to `<html>` as well as consumed in CSS: `dir` is what
 * makes logical properties (`padding-inline-start`) resolve, and screen readers
 * use it to choose voice and reading order. Setting only a wrapper element
 * leaves the document language wrong.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { DEFAULT_LOCALE, directionFor, isLocale, type Locale } from '@retailpro/shared';

import { translate, type MessageKey } from './strings';

export type Theme = 'light' | 'dark';

const LOCALE_KEY = 'retailpro.locale';
const THEME_KEY = 'retailpro.theme';

interface UiState {
  locale: Locale;
  direction: 'rtl' | 'ltr';
  theme: Theme;
  t: (key: MessageKey) => string;
  setLocale: (locale: Locale) => void;
  toggleLocale: () => void;
  toggleTheme: () => void;
}

const UiContext = createContext<UiState | null>(null);

function readStoredLocale(): Locale {
  const stored = localStorage.getItem(LOCALE_KEY);
  return isLocale(stored) ? stored : DEFAULT_LOCALE;
}

function readStoredTheme(): Theme {
  const stored = localStorage.getItem(THEME_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  // No explicit choice yet: follow the device. A cashier working a dim counter
  // or a bright shop floor should not have to override the system theme.
  return globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function UiProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readStoredLocale);
  const [theme, setTheme] = useState<Theme>(readStoredTheme);

  const direction = directionFor(locale);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = direction;
    localStorage.setItem(LOCALE_KEY, locale);
  }, [locale, direction]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  const setLocale = useCallback((next: Locale) => setLocaleState(next), []);
  const toggleLocale = useCallback(
    () => setLocaleState((current) => (current === 'ar' ? 'en' : 'ar')),
    [],
  );
  const toggleTheme = useCallback(
    () => setTheme((current) => (current === 'light' ? 'dark' : 'light')),
    [],
  );

  const value = useMemo<UiState>(
    () => ({
      locale,
      direction,
      theme,
      t: (key) => translate(locale, key),
      setLocale,
      toggleLocale,
      toggleTheme,
    }),
    [locale, direction, theme, setLocale, toggleLocale, toggleTheme],
  );

  return <UiContext.Provider value={value}>{children}</UiContext.Provider>;
}

export function useUi(): UiState {
  const value = useContext(UiContext);
  if (!value) throw new Error('useUi must be used inside <UiProvider>.');
  return value;
}
